import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  message,
  Spin,
  Modal,
  Form,
  Input,
  Select,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

const { Option } = Select;

const Teams = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [form] = Form.useForm();

  const fetchTeams = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await api.get("/teams", { params: { page, pageSize } });
      const { data, total } = response.data;
      const teamsWithExtras = data.map((team) => ({
        ...team,
        match_name: team.Match?.name || "-",
        captain_name: team.captain?.name || "-",
      }));

      setTeams(teamsWithExtras);
      setPagination((prev) => ({ ...prev, total, current: page }));
    } catch (err) {
      message.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchesAndPlayers = async () => {
    try {
      const [matchRes, playerRes] = await Promise.all([
        api.get("/matches"),
        api.get("/players"),
      ]);
      setMatches(matchRes.data.data || []);
      setPlayers(playerRes.data.data || []);
    } catch (err) {
      message.error("Failed to load matches or players");
    }
  };

  useEffect(() => {
    fetchTeams(pagination.current, pagination.pageSize);
    fetchMatchesAndPlayers();
  }, []);

  const handleDelete = async (teamId) => {
    try {
      await api.delete(`/teams/${teamId}`);
      message.success("Team deleted successfully");
      fetchTeams(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error("Failed to delete team");
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    form.setFieldsValue({
      name: team.name,
      match_id: team.Match?.match_id,
      captain_id: team.captain_id,
    });
    setIsEditModalVisible(true);
  };

  const handleOpenUploadModal = (team) => {
    setEditingTeam(team);
    setIsUploadModalVisible(true);
  };

  const handleUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("profile_pic", file);

    try {
      setUploading(true);
      const res = await api.post(
        `/teams/${editingTeam.team_id}/upload-profile`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data.success && res.data.data?.profile_pic) {
        const updatedUrl = res.data.data.profile_pic;
        setEditingTeam((prev) => ({ ...prev, profile_pic: updatedUrl }));
        message.success("Profile picture updated successfully");
        fetchTeams(pagination.current, pagination.pageSize);
        onSuccess?.(res, file);
      } else {
        onError?.(new Error("Invalid response"), file);
      }
    } catch (error) {
      onError?.(error, file);
      message.error("Failed to upload profile picture");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveTeam = async (values) => {
    try {
      if (editingTeam) {
        await api.put(`/teams/${editingTeam.team_id}`, values);
        message.success("Team updated successfully");
      } else {
        await api.post("/teams", values);
        message.success("Team created successfully");
      }

      setIsEditModalVisible(false);
      setEditingTeam(null);
      form.resetFields();
      fetchTeams(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error("Failed to save team");
    }
  };

  const columns = [
    {
      title: "Profile",
      dataIndex: "profile_pic",
      key: "profile_pic",
      render: (url, record) =>
        url ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img
              src={url}
              alt="Team"
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #ddd",
              }}
            />
            <Button size="small" onClick={() => handleOpenUploadModal(record)}>
              Change
            </Button>
          </div>
        ) : (
          <Button size="small" onClick={() => handleOpenUploadModal(record)}>
            Upload
          </Button>
        ),
    },
    { title: "Team Name", dataIndex: "name", key: "name" },
    { title: "Match", dataIndex: "match_name", key: "match_name" },
    { title: "Captain", dataIndex: "captain_name", key: "captain_name" },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          <Button type="link" onClick={() => handleEdit(record)}>
            Edit
          </Button>

          <Button
            type="link"
            onClick={() => navigate(`/dashboard/teams/${record.team_id}`)}
          >
            Details
          </Button>

          <Popconfirm
            title="Are you sure to delete this team?"
            onConfirm={() => handleDelete(record.team_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div style={{ paddingBottom: 20 }}>
      {/* Responsive Top Bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>Teams Management</h2>

        <Button
          type="primary"
          onClick={() => {
            setEditingTeam(null);
            form.resetFields();
            setIsEditModalVisible(true);
          }}
        >
          Create Team
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={teams}
          rowKey="team_id"
          pagination={pagination}
          onChange={(p) => fetchTeams(p.current, p.pageSize)}
          scroll={{ x: "max-content" }} // 👈 Makes table mobile-friendly!
        />
      )}

      {/* Edit/Create Modal */}
      <Modal
        title={editingTeam ? "Edit Team" : "Create Team"}
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingTeam(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="Save"
        width={450}
      >
        <Form form={form} layout="vertical" onFinish={handleSaveTeam}>
          <Form.Item
            name="name"
            label="Team Name"
            rules={[{ required: true, message: "Please enter team name" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="match_id"
            label="Match"
            rules={[{ required: true, message: "Please select match" }]}
          >
            <Select placeholder="Select match">
              {matches.map((match) => (
                <Option key={match.match_id} value={match.match_id}>
                  {match.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="captain_id" label="Captain (optional)">
            <Select placeholder="Select captain">
              {players.map((player) => (
                <Option key={player.player_id} value={player.player_id}>
                  {player.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Modal */}
      <Modal
        title="Update Team Profile Picture"
        open={isUploadModalVisible}
        onCancel={() => {
          setIsUploadModalVisible(false);
          setEditingTeam(null);
        }}
        footer={null}
        width={350}
      >
        {editingTeam && (
          <div style={{ textAlign: "center" }}>
            {editingTeam.profile_pic && (
              <img
                src={editingTeam.profile_pic}
                alt="Team"
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  objectFit: "cover",
                  marginBottom: 16,
                }}
              />
            )}

            <Upload customRequest={handleUpload} showUploadList={false}>
              <Button icon={<UploadOutlined />} loading={uploading}>
                {uploading ? "Uploading..." : "Upload New Picture"}
              </Button>
            </Upload>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Teams;
